import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  MessageSquare, Search, Filter, Trash2, ExternalLink,
  CheckCircle, Clock, AlertCircle, ChevronLeft, Loader2, Mail, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  new: { label: 'New', color: 'bg-blue-500/20 text-blue-400', icon: AlertCircle },
  reviewed: { label: 'Reviewed', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-500/20 text-green-400', icon: CheckCircle }
};

export default function AdminFeedbackPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchFeedback();
  }, [user, navigate, statusFilter]);

  const fetchFeedback = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      
      const { data } = await axios.get(`${API}/admin/feedback`, { params });
      setFeedback(data.feedback);
    } catch (error) {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const updateFeedbackStatus = async (feedbackId, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/admin/feedback/${feedbackId}`, {
        status: newStatus
      });
      toast.success('Status updated');
      fetchFeedback();
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const saveAdminNotes = async () => {
    if (!selectedFeedback) return;
    
    setUpdating(true);
    try {
      await axios.put(`${API}/admin/feedback/${selectedFeedback.id}`, {
        admin_notes: adminNotes
      });
      toast.success('Notes saved');
      fetchFeedback();
      setSelectedFeedback(prev => ({ ...prev, admin_notes: adminNotes }));
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setUpdating(false);
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm('Delete this feedback permanently?')) return;
    
    try {
      await axios.delete(`${API}/admin/feedback/${feedbackId}`);
      toast.success('Feedback deleted');
      fetchFeedback();
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback(null);
      }
    } catch (error) {
      toast.error('Failed to delete feedback');
    }
  };

  const openFeedbackDetail = (item) => {
    setSelectedFeedback(item);
    setAdminNotes(item.admin_notes || '');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-neutral-400 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <MessageSquare className="w-7 h-7 text-red-500" />
                User Feedback
              </h1>
              <p className="text-neutral-400 text-sm mt-1">
                {feedback.length} submissions
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-neutral-400" />
            <span className="text-sm text-neutral-400">Filter:</span>
          </div>
          <button
            onClick={() => setStatusFilter('')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              !statusFilter ? 'bg-white text-black' : 'bg-white/10 text-neutral-300 hover:bg-white/20'
            }`}
          >
            All
          </button>
          {Object.entries(statusConfig).map(([status, config]) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === status ? 'bg-white text-black' : 'bg-white/10 text-neutral-300 hover:bg-white/20'
              }`}
            >
              {config.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Feedback List */}
          <div className="space-y-4">
            {feedback.length === 0 ? (
              <div className="bg-[#121212] rounded-2xl border border-white/10 p-12 text-center">
                <MessageSquare className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                <p className="text-neutral-400">No feedback found</p>
              </div>
            ) : (
              feedback.map((item) => {
                const StatusIcon = statusConfig[item.status]?.icon || AlertCircle;
                return (
                  <div
                    key={item.id}
                    onClick={() => openFeedbackDetail(item)}
                    className={`bg-[#121212] rounded-xl border p-4 cursor-pointer transition-all hover:border-white/20 ${
                      selectedFeedback?.id === item.id ? 'border-red-500' : 'border-white/10'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <Badge className={statusConfig[item.status]?.color}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig[item.status]?.label}
                      </Badge>
                      <span className="text-xs text-neutral-500">
                        {format(new Date(item.created_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                    
                    <p className="text-white text-sm line-clamp-3 mb-3">
                      {item.message}
                    </p>
                    
                    <div className="flex items-center gap-4 text-xs text-neutral-500">
                      {item.contact_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {item.contact_name}
                        </span>
                      )}
                      {item.contact_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {item.contact_email}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Detail Panel */}
          {selectedFeedback && (
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 h-fit sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Feedback Details</h3>
                <button
                  onClick={() => deleteFeedback(selectedFeedback.id)}
                  className="text-red-400 hover:text-red-300 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              {/* Status */}
              <div className="mb-6">
                <p className="text-sm text-neutral-400 mb-2">Status</p>
                <div className="flex gap-2">
                  {Object.entries(statusConfig).map(([status, config]) => (
                    <button
                      key={status}
                      onClick={() => updateFeedbackStatus(selectedFeedback.id, status)}
                      disabled={updating}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedFeedback.status === status 
                          ? config.color 
                          : 'bg-white/5 text-neutral-400 hover:bg-white/10'
                      }`}
                    >
                      {config.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              {(selectedFeedback.contact_name || selectedFeedback.contact_email) && (
                <div className="mb-6 p-4 bg-white/5 rounded-xl">
                  <p className="text-sm text-neutral-400 mb-2">Contact Info</p>
                  {selectedFeedback.contact_name && (
                    <p className="text-white flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-neutral-500" />
                      {selectedFeedback.contact_name}
                    </p>
                  )}
                  {selectedFeedback.contact_email && (
                    <a 
                      href={`mailto:${selectedFeedback.contact_email}`}
                      className="text-red-400 hover:text-red-300 flex items-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      {selectedFeedback.contact_email}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}

              {/* Message */}
              <div className="mb-6">
                <p className="text-sm text-neutral-400 mb-2">Message</p>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-white whitespace-pre-wrap text-sm">
                    {selectedFeedback.message}
                  </p>
                </div>
              </div>

              {/* Admin Notes */}
              <div className="mb-6">
                <p className="text-sm text-neutral-400 mb-2">Admin Notes</p>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes..."
                  className="bg-[#0a0a0a] border-white/10 text-white min-h-[100px]"
                />
                <Button
                  onClick={saveAdminNotes}
                  disabled={updating}
                  className="mt-2 bg-red-500 hover:bg-red-600"
                  size="sm"
                >
                  {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Notes'}
                </Button>
              </div>

              {/* Timestamp */}
              <p className="text-xs text-neutral-500">
                Submitted: {format(new Date(selectedFeedback.created_at), 'MMMM d, yyyy at h:mm a')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
